import { useEffect, useState, type FormEvent } from 'react';
import { ArrowLeft, Mail, WifiOff } from 'lucide-react';
import { Button } from '../components/Button';
import { Field, Input } from '../components/Field';
import { sendSignInCode, verifySignInCode } from './session';

const RESEND_SECONDS = 60;

function friendlyError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  if (/rate limit|too many|security purposes/i.test(message)) return 'Too many attempts. Please wait a minute and try again.';
  if (/expired|invalid|token/i.test(message)) return 'That code is wrong or has expired. Check the latest email, or send a new code.';
  if (/fetch|network/i.test(message)) return 'Can’t reach RenalFlow. Check your internet connection.';
  return message;
}

export function SignInPage() {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  async function sendCode(event?: FormEvent) {
    event?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await sendSignInCode(email.trim().toLowerCase());
      setStep('code');
      setCode('');
      setResendIn(RESEND_SECONDS);
    } catch (caught) {
      setError(friendlyError(caught));
    } finally {
      setBusy(false);
    }
  }

  async function verify(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifySignInCode(email.trim().toLowerCase(), code.trim());
      // The auth store picks up the new session and the app takes over from here.
    } catch (caught) {
      setError(friendlyError(caught));
      setBusy(false);
    }
  }

  return (
    <div className="safe-top flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <img src="/icon.svg" alt="" className="mx-auto mb-4 h-16 w-16 rounded-2xl" />
          <h1 className="text-2xl font-semibold tracking-tight">RenalFlow</h1>
          <p className="mt-1 text-sm text-clinical-muted">Your personal dialysis log</p>
        </div>

        {!online ? (
          <p className="mb-4 flex items-center gap-2 rounded-lg border border-amber-300/60 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300">
            <WifiOff className="h-4 w-4 shrink-0" /> You’re offline. Signing in needs an internet connection the first time.
          </p>
        ) : null}

        <div className="rounded-xl border border-clinical-line bg-clinical-card p-5 shadow-soft">
          {step === 'email' ? (
            <form onSubmit={sendCode} className="space-y-4">
              <h2 className="text-lg font-semibold">Sign in</h2>
              <p className="text-sm text-clinical-muted">We’ll email you a 6-digit code. New here? The same code creates your account.</p>
              <Field label="Email">
                <Input
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                />
              </Field>
              {error ? <p className="text-sm text-clinical-danger">{error}</p> : null}
              <Button type="submit" className="w-full" disabled={busy || !online || !email.includes('@')}>
                <Mail className="h-4 w-4" /> {busy ? 'Sending…' : 'Email me a code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4">
              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setError(null);
                }}
                className="focus-ring -ml-1 flex items-center gap-1 rounded text-sm text-clinical-muted hover:text-clinical-ink"
              >
                <ArrowLeft className="h-4 w-4" /> Use a different email
              </button>
              <h2 className="text-lg font-semibold">Check your email</h2>
              <p className="text-sm text-clinical-muted">
                We sent a code to <span className="font-medium text-clinical-ink">{email}</span>.
              </p>
              <Field label="6-digit code">
                <Input
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-[0.5em]"
                  aria-label="6-digit code"
                />
              </Field>
              {error ? <p className="text-sm text-clinical-danger">{error}</p> : null}
              <Button type="submit" className="w-full" disabled={busy || code.length !== 6}>
                {busy ? 'Checking…' : 'Sign in'}
              </Button>
              <Button type="button" variant="ghost" className="w-full" disabled={busy || resendIn > 0} onClick={() => void sendCode()}>
                {resendIn > 0 ? `Send a new code in ${resendIn}s` : 'Send a new code'}
              </Button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-clinical-muted">
          Just looking?{' '}
          <a href="/?demo=1" className="font-semibold text-clinical-primary hover:underline">
            Try the demo
          </a>
        </p>
      </div>
    </div>
  );
}
